import { useCallback } from 'react';
import type { CprModificationTool } from 'config/config';

type CprModificationConfig = {
  cprModificationTool?: CprModificationTool;
  cprSculptSize?: number;
  cprPixelBrushSize?: number;
  cprPixelBrushMode?: string;
};

type SetConfigData = (data: Record<string, unknown>) => void;

export function useCprModificationSettings({
  config,
  setData,
}: {
  config: CprModificationConfig;
  setData: SetConfigData;
}) {
  const cprModificationTool = config.cprModificationTool ?? 'none';
  const cprSculptSize = Math.max(
    1,
    Math.min(20, Number(config.cprSculptSize) || 5),
  );
  const cprPixelBrushSize = Math.max(
    1,
    Math.min(30, Number(config.cprPixelBrushSize) || 5),
  );
  const cprPixelBrushMode =
    config.cprPixelBrushMode === 'erase' ? 'erase' : 'draw';

  const handleCprModificationToolToggle = useCallback(
    (tool: Exclude<CprModificationTool, 'none'>) => {
      setData({
        cprModificationTool: cprModificationTool === tool ? 'none' : tool,
      });
    },
    [cprModificationTool, setData],
  );

  const handleCprSculptSizeChange = useCallback(
    (nextSize: number) => {
      const clamped = Math.max(1, Math.min(20, Math.round(nextSize || 1)));
      if (clamped === cprSculptSize) return;
      setData({ cprSculptSize: clamped });
    },
    [cprSculptSize, setData],
  );

  const handleCprPixelBrushSizeChange = useCallback(
    (nextSize: number) => {
      const clamped = Math.max(1, Math.min(30, Math.round(nextSize || 1)));
      if (clamped === cprPixelBrushSize) return;
      setData({ cprPixelBrushSize: clamped });
    },
    [cprPixelBrushSize, setData],
  );

  const handleCprPixelBrushModeChange = useCallback(
    (mode: 'draw' | 'erase') => {
      if (mode === cprPixelBrushMode) return;
      setData({ cprPixelBrushMode: mode });
    },
    [cprPixelBrushMode, setData],
  );

  return {
    cprModificationTool,
    cprSculptSize,
    cprPixelBrushSize,
    cprPixelBrushMode,
    handleCprModificationToolToggle,
    handleCprSculptSizeChange,
    handleCprPixelBrushSizeChange,
    handleCprPixelBrushModeChange,
  };
}
